import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Search, Filter, MoreVertical, Edit, Trash2, Eye } from 'lucide-react';
import { theme } from '../../styles/theme';
import { Card } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Modal } from '../../components/UI/Modal';
import { useApiOnMount, useApi } from '../../hooks/useApi';
import { spaceService } from '../../services/spaceService';
import { formatRelativeTime } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const SpacesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex: 1;
  max-width: 500px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    max-width: none;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing['2xl']};
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.gray400};
  z-index: 1;
`;

const FilterButton = styled(Button)`
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const SpacesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const SpaceCard = styled(Card)`
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const SpaceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SpaceColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color || theme.colors.primary};
  flex-shrink: 0;
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const SpaceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SpaceName = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpaceDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SpaceStats = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
`;

const Stat = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatValue = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const SpaceActions = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  
  ${SpaceCard}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.gray600};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray100};
    color: ${({ theme }) => theme.colors.gray700};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 150px;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  transition: background-color 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray100};
  }
  
  &:first-child {
    border-radius: ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md} 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  h3 {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ColorPicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const ColorOption = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  border: 2px solid ${({ $selected, theme }) => 
    $selected ? theme.colors.primary : 'transparent'
  };
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const colorOptions = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
  '#10B981', '#EF4444', '#06B6D4', '#84CC16',
  '#F97316', '#6B7280', '#8B5A2B', '#BE185D'
];

function SpacesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSpace, setEditingSpace] = useState(null);
  const [showActions, setShowActions] = useState(null);
  
  const { data: spaces, loading, refetch } = useApiOnMount(spaceService.getSpaces);
  const { execute: createSpace, loading: creating } = useApi(spaceService.createSpace);
  const { execute: updateSpace, loading: updating } = useApi(spaceService.updateSpace);
  const { execute: deleteSpace, loading: deleting } = useApi(spaceService.deleteSpace);

  const filteredSpaces = spaces?.filter(space =>
    space.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    space.description?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const handleCreateSpace = async (data) => {
    try {
      await createSpace(data);
      toast.success('Space created successfully');
      setShowCreateModal(false);
      refetch();
    } catch (error) {
      toast.error('Failed to create space');
    }
  };

  const handleUpdateSpace = async (id, data) => {
    try {
      await updateSpace(id, data);
      toast.success('Space updated successfully');
      setEditingSpace(null);
      refetch();
    } catch (error) {
      toast.error('Failed to update space');
    }
  };

  const handleDeleteSpace = async (id) => {
    if (window.confirm('Are you sure you want to delete this space? This action cannot be undone.')) {
      try {
        await deleteSpace(id);
        toast.success('Space deleted successfully');
        refetch();
      } catch (error) {
        toast.error('Failed to delete space');
      }
    }
  };

  const handleSpaceClick = (spaceId) => {
    navigate(`/spaces/${spaceId}`);
  };

  return (
    <SpacesContainer>
      <Header>
        <Title>Spaces</Title>
        <Controls>
          <SearchContainer>
            <SearchIcon>
              <Search size={18} />
            </SearchIcon>
            <SearchInput
              placeholder="Search spaces..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>
          <FilterButton variant="secondary">
            <Filter size={16} />
            Filter
          </FilterButton>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            New Space
          </Button>
        </Controls>
      </Header>

      {loading ? (
        <div>Loading spaces...</div>
      ) : filteredSpaces.length > 0 ? (
        <SpacesGrid>
          {filteredSpaces.map((space) => (
            <SpaceCard
              key={space.id}
              onClick={() => handleSpaceClick(space.id)}
            >
              <SpaceHeader>
                <SpaceColor $color={space.color} />
                <SpaceInfo>
                  <SpaceName>{space.name}</SpaceName>
                  <SpaceDescription>
                    {space.description || 'No description'}
                  </SpaceDescription>
                </SpaceInfo>
                <SpaceActions>
                  <ActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(showActions === space.id ? null : space.id);
                    }}
                  >
                    <MoreVertical size={16} />
                  </ActionButton>
                  {showActions === space.id && (
                    <Dropdown>
                      <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSpace(space);
                          setShowActions(null);
                        }}
                      >
                        <Edit size={14} />
                        Edit
                      </DropdownItem>
                      <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSpace(space.id);
                          setShowActions(null);
                        }}
                      >
                        <Trash2 size={14} />
                        Delete
                      </DropdownItem>
                    </Dropdown>
                  )}
                </SpaceActions>
              </SpaceHeader>
              <SpaceStats>
                <Stat>
                  <StatValue>{space.secrets?.length || 0}</StatValue> secrets
                </Stat>
                <Stat>
                  Updated {formatRelativeTime(space.updated_at)}
                </Stat>
              </SpaceStats>
            </SpaceCard>
          ))}
        </SpacesGrid>
      ) : (
        <EmptyState>
          <h3>No spaces found</h3>
          <p>
            {searchQuery 
              ? 'No spaces match your search criteria'
              : 'Create your first space to start organizing your secrets'
            }
          </p>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Create Space
          </Button>
        </EmptyState>
      )}

      {/* Create/Edit Modal */}
      <SpaceModal
        isOpen={showCreateModal || !!editingSpace}
        onClose={() => {
          setShowCreateModal(false);
          setEditingSpace(null);
        }}
        onSubmit={editingSpace ? 
          (data) => handleUpdateSpace(editingSpace.id, data) :
          handleCreateSpace
        }
        initialData={editingSpace}
        loading={creating || updating}
      />
    </SpacesContainer>
  );
}

export default SpacesPage;

// Space Modal Component
function SpaceModal({ isOpen, onClose, onSubmit, initialData, loading }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    ...initialData
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({ ...prev, color }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Edit Space' : 'Create New Space'}
      maxWidth="500px"
    >
      <Form onSubmit={handleSubmit}>
        <Input
          label="Space Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter space name"
          required
        />
        
        <Input
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter space description (optional)"
        />
        
        <div>
          <label style={{ 
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.gray700,
            marginBottom: theme.spacing.sm,
            display: 'block'
          }}>
            Color
          </label>
          <ColorPicker>
            {colorOptions.map((color) => (
              <ColorOption
                key={color}
                $color={color}
                $selected={formData.color === color}
                onClick={() => handleColorSelect(color)}
                type="button"
              />
            ))}
          </ColorPicker>
        </div>

        <div style={{ 
          display: 'flex', 
          gap: theme.spacing.md, 
          justifyContent: 'flex-end',
          marginTop: theme.spacing.lg
        }}>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            {initialData ? 'Update Space' : 'Create Space'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
